// Firestore imports removed
import { ApiService } from "../services/api_service"; // Adjust path as needed

// updateAvailableTickets removed as it should be handled by backend

/**
 * Creates a booking record via API
 * @param {string} paymentId - Payment ID from Razorpay
 * @param {object} bookingData - All booking related data
 * @param {object} user - Current user object
 */
export const createBookingData = async (paymentId, bookingData, user) => {
  try {
    console.log("📝 Creating booking record via API...");

    const {
      eventId,
      eventName,
      eventLocation,
      selectedDate,
      selectedSlot,
      selectedTickets,
      totalPrice,
      grandTotal,
      ticketDetails
    } = bookingData;

    // Calculate total tickets and total persons allowed (logic preserved for logging or verification if needed)
    const totalTicketsCount = Object.values(selectedTickets).reduce((sum, quantity) => sum + quantity, 0);

    let totalPersonsAllowed = 0;
    // let ticketBreakdown = []; // breakdown logic not strictly needed for API payload if backend constructs it

    selectedSlot.tickets.forEach(ticket => {
      if (selectedTickets[ticket.id]) {
        const quantity = selectedTickets[ticket.id];
        const personsForThisTicket = quantity * (ticket.personAllowedPerTicket || 1);
        totalPersonsAllowed += personsForThisTicket;
      }
    });

    // Get the primary ticket type for the main ticketType field
    const primaryTicketType = selectedSlot.tickets.find(ticket => selectedTickets[ticket.id]);

    // Convert dates to ISO strings
    const ticketDateString = selectedDate.dateOfEvent.toDate ?
      selectedDate.dateOfEvent.toDate().toISOString() :
      new Date(selectedDate.dateOfEvent).toISOString();

    const ticketSlotString = selectedSlot.startTime.toDate ?
      selectedSlot.startTime.toDate().toISOString() :
      new Date(selectedSlot.startTime).toISOString();

    const createdAtString = new Date().toISOString();

    // Booking payload matching API expectation
    // UserData model suggests bookingIdsList is string list.
    // Booking model provided in prompt:
    /*
    class Booking {
      final String id;
      final String paymentId;
      final String eventId;
      final DateTime createdAt;
      final DateTime eventDate;
      final DateTime ticketSlot;
      final String ticketId;
      final String ticketName;
      final double ticketPrice;
      final int personAllowedPerTicket;
      final String ticketDescription;
      final bool isRefundable;
      final int ticketCount;
      final bool isCheckIn;
      final int totalUserAllowed;
      final int numberOfUserCheckedIn;
      final String userId;
      final String userName;
      final String userPhone;
      final String userEmail;
    }
    */

    // Construct the flat Map<String, dynamic> as requested by prompt
    const apiBookingData = {
      paymentId: paymentId,
      eventId: eventId,
      dateId: selectedDate.id,
      slotId: selectedSlot.id,
      createdAt: createdAtString, // Or let backend handle it
      eventDate: ticketDateString, // Note: Model says eventDate, previous code said ticketDate. Prompt says EventDate. Using eventDate key to match Model? 
      // Previous code used 'ticketDate'. Backend might expect specific key. 
      // Prompt Booking class has 'eventDate'. I'll use 'eventDate' and 'ticketDate' to be safe? 
      // Wait, 'params' in doc says 'bookingData (Map<String, dynamic>)'.
      // I'll stick to the keys used in previous code if they worked directly with Firestore, 
      // BUT backend model usually maps from JSON. 
      // The prompt provided Dart model uses camelCase.
      // previous code: ticketDate, ticketSlot. 
      // prompt: eventDate, ticketSlot.
      // I will include ALL keys to be safe.
      ticketDate: ticketDateString,
      eventDate: ticketDateString,

      ticketSlot: ticketSlotString,

      ticketId: primaryTicketType.id,
      ticketName: primaryTicketType.name,
      ticketPrice: primaryTicketType.price,
      personAllowedPerTicket: primaryTicketType.personAllowedPerTicket || 1,
      ticketDescription: primaryTicketType.description || "",
      isRefundable: primaryTicketType.isRefundable || false,
      ticketCount: totalTicketsCount,

      isCheckIn: false,
      totalUserAllowed: totalPersonsAllowed,
      numberOfUserCheckedIn: 0,

      userId: user.uid,
      userName: ticketDetails.name,
      userPhone: ticketDetails.phone,
      userEmail: ticketDetails.email,
    };

    console.log("📄 Booking API payload:", apiBookingData);

    // Call API
    // Note: createBooking returns ApiResponse<Map<String, dynamic>>
    const response = await ApiService.createBooking(apiBookingData);

    // Support response.data or direct response
    const responseData = response.data || response;

    // Assuming backend returns the created booking or its ID.
    // Use ID from response or fallback if necessary (backend usually generates ID)
    const newBookingId = responseData.id || responseData.bookingId;

    if (!newBookingId) {
      console.warn("API response didn't contain booking ID", response);
      // Throwing error might interrupt flow, but if success is true...
      // Maybe return 'unknown' or rely on what IS returned.
    }

    console.log("✅ Booking created via API with ID:", newBookingId);
    return newBookingId;

  } catch (error) {
    console.error("❌ Error creating booking via API:", error);
    throw error;
  }
};

/**
 * Main payment success handler
 * @param {string} paymentId - Payment ID from Razorpay
 * @param {object} checkoutData - All checkout related data
 * @param {object} user - Current user object
 * @param {function} setShowPaymentModal - Function to hide payment modal
 * @param {function} onSuccess - Success callback
 * @param {function} onError - Error callback
 */
export const handlePaymentSuccess = async (
  paymentId,
  checkoutData,
  user,
  setShowPaymentModal,
  onSuccess,
  onError
) => {
  try {
    console.log("🎉 Payment successful! Starting post-payment processing...");
    console.log("💳 Payment ID:", paymentId);
    console.log("📋 Received checkoutData:", checkoutData);
    console.log("👤 User:", user);

    // Validate inputs
    if (!paymentId) {
      throw new Error("Payment ID is missing");
    }

    if (!checkoutData) {
      throw new Error("Checkout data is missing");
    }

    if (!user || !user.uid) {
      throw new Error("User data is missing");
    }

    const {
      eventId,
      selectedSlot,
      selectedTickets,
      selectedDate,
      eventData,
      totalPrice,
      grandTotal,
      ticketDetails,
      eventName,
      eventLocation
    } = checkoutData;

    // Validate required fields
    const requiredFields = {
      eventId,
      selectedSlot,
      selectedTickets,
      selectedDate,
      ticketDetails,
      eventName,
      eventLocation
    };

    for (const [key, value] of Object.entries(requiredFields)) {
      if (!value) {
        throw new Error(`Required field missing: ${key}`);
      }
    }

    console.log("✅ All required data validated");
    console.log("🎯 Event ID:", eventId);
    console.log("📅 Selected Date:", selectedDate);
    console.log("⏰ Selected Slot:", selectedSlot);
    console.log("🎫 Selected Tickets:", selectedTickets);

    // Step 1: Update ticket availability
    // Handled by backend createBooking
    console.log("📊 Step 1: Ticket availability updated by backend...");
    // await updateAvailableTickets(eventId, selectedDate, selectedSlot, selectedTickets);

    // Step 2: Create booking record
    console.log("📝 Step 2: Creating booking record...");
    const bookingId = await createBookingData(paymentId, {
      eventId,
      eventName,
      eventLocation,
      selectedDate,
      selectedSlot,
      selectedTickets,
      totalPrice,
      grandTotal,
      ticketDetails
    }, user);

    // Step 3: Update UI state
    console.log("🎨 Step 3: Updating UI state...");
    setShowPaymentModal(false);

    // Call success callback
    if (onSuccess) {
      onSuccess(bookingId);
    }

    console.log("🎊 Payment processing completed successfully!");

    return {
      success: true,
      bookingId: bookingId,
      message: "Booking confirmed successfully!"
    };

  } catch (error) {
    console.error("💥 Payment success handler error:", error);

    // Hide payment modal on error
    setShowPaymentModal(false);

    // Call error callback
    if (onError) {
      onError(error.message);
    }

    return {
      success: false,
      error: error.message,
      message: "Failed to complete booking. Please contact support."
    };
  }
}; 